import React, { useContext, useState } from "react";
import { TeamManagementContext } from "./teammanagementContext";
import ApiActions from "../../actions/ApiActions";
import styles from "../../assets/stylesheets/pages/teammanagement/teamlead.module.scss";
import FutworkButton from "../../uicomponents/button";
import { InfoIcon } from "../../assets/images";
import checkIsEmpty from "../../helper/checkIsEmpty";

const AddAgentsSidePanel = (props) => {
    const { teamSelected } = useContext(TeamManagementContext);
    const [mobileNumbersText, setMobileNumbersText] = useState('');
    const [loading, setLoading] = useState(false);
    const [apiMsg, setApiMsg] = useState({ type: '', msg: '' });

    const parseMobileNumbers = (text) => {
        // Split by comma, newline, or space, filter valid numbers
        return text
            .split(/\s|,|\n/)
            .map(num => num.trim())
            .filter(num => num.length > 0);
    };


    const handleTextareaChange = (e) => {
        const value = e.target.value;
        setMobileNumbersText(value);
        
        // Clear any existing validation errors when user types
        if (apiMsg.type === 'error' && apiMsg.msg.includes('Please enter 10 digit mobile number')) {
            setApiMsg({ type: '', msg: '' });
        }
    };

    const handleSubmit = async () => {
        const mobileNumbers = parseMobileNumbers(mobileNumbersText);
        if (mobileNumbers.length === 0) {
            setApiMsg({ type: 'error', msg: 'Please enter at least one mobile number.' });
            return;
        }

        // Validation for maximum 100 mobile numbers
        if (mobileNumbers.length > 100) {
            setApiMsg({ type: 'error', msg: 'Mobile Number should be less than 100' });
            return;
        }

        // Validation for 10-digit numbers
        const invalidNumbers = mobileNumbers.filter(num => !/^[0-9]{10}$/.test(num));
        if (invalidNumbers.length > 0) {
            const invalidNumbersList = invalidNumbers.join(', ');
            setApiMsg({ type: 'error', msg: `Please enter 10 digit mobile number: ${invalidNumbersList}` });
            return;
        }

        // Validation for repeated numbers
        const numberCount = {};
        const repeatedNumbers = [];
        mobileNumbers.forEach(num => {
            numberCount[num] = (numberCount[num] || 0) + 1;
        });
        Object.keys(numberCount).forEach(num => {
            if (numberCount[num] > 1) {
                repeatedNumbers.push(num);
            }
        });
        if (repeatedNumbers.length > 0) {
            setApiMsg({ type: 'error', msg: `Repeated mobile numbers: ${repeatedNumbers.join(', ')}` });
            return;
        }

        setLoading(true);
        setApiMsg({ type: '', msg: '' });
        
        try {
            const payload = {
                agents: mobileNumbers.map(num => parseInt(num)),
                teamLeadId: (teamSelected && teamSelected.id) || (teamSelected && teamSelected.teamLeadId)
            };
            
            const result = await ApiActions.addAgentToTL(payload);
            
            if (result.status === 200) {
                setApiMsg({ type: 'success', msg: (result.data && result.data.msg) || 'Agents assigned successfully.' });
                setMobileNumbersText('');
                // Refresh the data after successful assignment
                if (props.fetchData) {
                    props.fetchData();
                }
                // Close the panel after successful assignment
                setTimeout(() => {
                    props.setShowAddAgentPanel(false);
                }, 10000);
            } else {
                const errorMsg = (result.data && (result.data.msg || result.data.error)) || 'Something went wrong.';
                
                // Split error message by semicolon to handle combined errors
                const errorParts = errorMsg.split(';').map(part => part.trim()).filter(part => part.length > 0);
                
                // If no semicolons found, treat the entire message as a single error
                const partsToProcess = errorParts.length > 0 ? errorParts : [errorMsg];
                
                // Separate "already assigned to team" errors from other errors
                const alreadyAssignedErrors = partsToProcess.filter(part => part.includes('already assigned to a team'));
                const otherErrors = partsToProcess.filter(part => !part.includes('already assigned to a team'));
                
                if (alreadyAssignedErrors.length > 0 || otherErrors.length > 0) {
                    // Format already assigned errors to show main message and agents on separate lines
                    const formattedAlreadyAssignedErrors = alreadyAssignedErrors.map(error => {
                        if (error.includes(':')) {
                            const [mainMessage, agentsList] = error.split(':');
                            const agents = agentsList.trim().split(', ');
                            return `${mainMessage}:\n${agents.join('\n')}`;
                        }
                        return error;
                    });
                    
                    setApiMsg({ 
                        type: 'error', 
                        msg: otherErrors.join('\n'),
                        alreadyAssignedErrors: formattedAlreadyAssignedErrors
                    });
                } else {
                    setApiMsg({ type: 'error', msg: errorMsg });
                }
            }
        } catch (error) {
            const errorMsg = (error && error.response && error.response.data && (error.response.data.msg || error.response.data.error)) || 'Something went wrong.';
            
            // Split error message by semicolon to handle combined errors
            const errorParts = errorMsg.split(';').map(part => part.trim()).filter(part => part.length > 0);
            
            // If no semicolons found, treat the entire message as a single error
            const partsToProcess = errorParts.length > 0 ? errorParts : [errorMsg];
            
            // Separate "already assigned to team" errors from other errors
            const alreadyAssignedErrors = partsToProcess.filter(part => part.includes('already assigned to a team'));
            const otherErrors = partsToProcess.filter(part => !part.includes('already assigned to a team'));
            
            if (alreadyAssignedErrors.length > 0 || otherErrors.length > 0) {
                // Format already assigned errors to show main message and agents on separate lines
                const formattedAlreadyAssignedErrors = alreadyAssignedErrors.map(error => {
                    if (error.includes(':')) {
                        const [mainMessage, agentsList] = error.split(':');
                        const agents = agentsList.trim().split(', ');
                        return `${mainMessage}:\n${agents.join('\n')}`;
                    }
                    return error;
                });
                
                setApiMsg({ 
                    type: 'error', 
                    msg: otherErrors.join('\n'),
                    alreadyAssignedErrors: formattedAlreadyAssignedErrors
                });
            } else {
                setApiMsg({ type: 'error', msg: errorMsg });
            }
        } finally {
            setLoading(false);
        }
    };

    // Add a variable to check if at least one mobile number is present
    const hasAtLeastOneMobile = parseMobileNumbers(mobileNumbersText).length > 0;
    const isSubmitDisabled = loading || !hasAtLeastOneMobile;

    return (
        <div className={'modalSidebarContainer'}>
            <div className={styles.contentArea}>
                <div className={styles['dotted-border']}>
                    <div style={{width: '100%'}}>
                        <b>Enter Agent's Mobile Number</b>
                        <textarea
                            style={{ width: '100%', minHeight: '120px', marginTop: '8px', marginBottom: '8px', padding: '8px', fontSize: '1em', boxSizing: 'border-box' }}
                            placeholder="Enter Agent's Mobile Numbers separated by comma or newline"
                            value={mobileNumbersText}
                            onChange={handleTextareaChange}
                            disabled={loading}
                        />
                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                            <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
                                <b>NOTE:</b> Maximum limit is 100 mobile numbers
                            </div>
                            <div style={{ fontSize: '13px', color: '#555', marginTop: '2px' }}>
                                Mobile numbers : {parseMobileNumbers(mobileNumbersText).length}
                            </div>
                        </div>
                    </div>
                </div>

                
                {/* Show already assigned errors in yellow warning box - positioned after the note */}
                {apiMsg.type && apiMsg.alreadyAssignedErrors && apiMsg.alreadyAssignedErrors.length > 0 && (
                    <div className={styles['warning-msg']}>
                        {apiMsg.alreadyAssignedErrors.map((error, idx) => (
                            <div key={idx} style={{ whiteSpace: 'pre-line' }}>{error}</div>
                        ))}
                    </div>
                )}
            </div>
            <div className={styles['criteria-footer']}>
                <div className={styles['first-child']}>
                    {!checkIsEmpty(apiMsg) && apiMsg.type && (
                        <>
                            {/* Show other errors in regular error format */}
                            {apiMsg.msg && apiMsg.msg.length > 0 && (
                                apiMsg.type === 'error' && typeof apiMsg.msg === 'string' && apiMsg.msg.includes('\n') ? (
                                    apiMsg.msg.split('\n').map((line, idx) => (
                                        <div key={idx} className={styles['error-msg']}>{line}</div>
                                    ))
                                ) : (
                                    <div className={apiMsg.type === 'success' ? styles['success-msg'] : styles['error-msg']}>
                                        {apiMsg.msg}
                                    </div>
                                )
                            )}
                        </>
                    )}
                </div>
                <div className={styles['second-child']}>
                    <FutworkButton
                        buttonStyle="primary--outline non-rounded"
                        buttonSize="sm"
                        onClick={() => props.setShowAddAgentPanel(false)}
                        disabled={loading}
                    >
                        Cancel
                    </FutworkButton>
                    <FutworkButton 
                        buttonStyle='navy--solid non-rounded' 
                        buttonSize="sm" 
                        onClick={handleSubmit} 
                        disabled={isSubmitDisabled}
                    >
                        Submit
                    </FutworkButton>
                </div>
            </div>
        </div>
    );
};

export default AddAgentsSidePanel;
